import { useCallback, useEffect, useMemo, useState } from 'react'

export type SortDir = 'asc' | 'desc'

export type SortAccessor<T> = (row: T) => string | number | null | undefined

function compareValues(a: string | number | null | undefined, b: string | number | null | undefined) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base', numeric: true })
}

function readSortStorage(storageKey: string, defaultKey?: string, defaultDir: SortDir = 'asc') {
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return { sortKey: defaultKey ?? null, sortDir: defaultDir }
    const parsed = JSON.parse(raw) as { sortKey?: string | null; sortDir?: SortDir }
    return {
      sortKey: parsed.sortKey ?? defaultKey ?? null,
      sortDir: parsed.sortDir === 'asc' || parsed.sortDir === 'desc' ? parsed.sortDir : defaultDir,
    }
  } catch {
    return { sortKey: defaultKey ?? null, sortDir: defaultDir }
  }
}

export function useTableSort<T>(defaultKey?: string, defaultDir: SortDir = 'asc', storageKey?: string) {
  const [sort, setSort] = useState<{ sortKey: string | null; sortDir: SortDir }>(() =>
    storageKey
      ? readSortStorage(storageKey, defaultKey, defaultDir)
      : { sortKey: defaultKey ?? null, sortDir: defaultDir },
  )
  const { sortKey, sortDir } = sort

  useEffect(() => {
    if (!storageKey) return
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ sortKey, sortDir }))
    } catch {
      /* quota / private mode */
    }
  }, [sortKey, sortDir, storageKey])

  const toggleSort = useCallback((key: string) => {
    setSort((prev) =>
      prev.sortKey === key
        ? { sortKey: key, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' }
        : { sortKey: key, sortDir: 'asc' },
    )
  }, [])

  const sortRows = useCallback(
    (rows: T[], accessors: Record<string, SortAccessor<T>>) => {
      if (!sortKey || !accessors[sortKey]) return rows
      const accessor = accessors[sortKey]
      const dir = sortDir === 'asc' ? 1 : -1
      return [...rows].sort((a, b) => compareValues(accessor(a), accessor(b)) * dir)
    },
    [sortKey, sortDir],
  )

  return useMemo(
    () => ({ sortKey, sortDir, toggleSort, sortRows }),
    [sortKey, sortDir, toggleSort, sortRows],
  )
}
